import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	NativeSelect,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { useWorkerTypesContext } from "@/pages/configurations/workerTypes/context/WorkerTypesContext";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Worker } from "../../models/Worker";
import {
	getWorkerData,
	updateWorker,
} from "../../repositories/WorkersRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	workerIdForEdit: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditWorker = ({ workerIdForEdit, show, onHide }: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [workerForEdit, setWorkerForEdit] = useState<Worker | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const {
		state: { workerTypes },
	} = useWorkerTypesContext();

	const {
		register,
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Worker>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<Worker> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await updateWorker(workerIdForEdit, data);
			if (res.ok) {
				setMessage("The worker was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const worker = await getWorkerData(workerIdForEdit);
				setWorkerForEdit(worker?.data?.data.result);

				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && workerForEdit) {
			const { workerId, workerName, isActive, workerTypeId } = workerForEdit!;
			setValue("workerId", workerId);
			setValue("workerName", workerName);
			setValue("isActive", isActive);
			setValue("workerTypeId", workerTypeId);
		}
	}, [workerForEdit]);

	return (
		<>
			<>
				<Modal
					disableEnforcedFocus={false}
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="editWorker"
					title="Edit worker"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "250px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={12} xs={12}>
											<Controller
												name="workerName"
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.workerName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.workerName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="workerTypeId"
												control={control}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															Worker type
														</InputLabel>
														<NativeSelect
															value={field.value}
															onChange={field.onChange}
														>
															{workerTypes.map((type) => {
																return (
																	<option
																		value={type.workerTypeId}
																		key={type.workerTypeId}
																	>
																		{type.workerTypeName}
																	</option>
																);
															})}
														</NativeSelect>
													</FormControl>
												)}
											/>
										</Grid>
										<Grid item md={8} xs={12}>
											<Typography>Status</Typography>
											<Controller
												name="isActive"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Active" : "Inactive"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setWorkerForEdit(null);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
